// src/lib/setupApiAuth.js
// One-shot, run-at-boot setup that:
//   1) Monkey-patches window.fetch so every request to our backend
//      (environment.apiURL host) carries Authorization: Bearer <token>
//      when a token is stored. Other fetches (CDN, Firebase, third-party)
//      pass through untouched.
//   2) Adds the same header to axios via a global request interceptor.
//   3) On a 401 from the backend, clears the local auth state. The user
//      stays on the current page; navigation/redirect is left to the
//      callers (Login.jsx, future ProtectedRoute) so we don't ambush
//      anyone mid-action.
//
// Why monkey-patch instead of a wrapper:
//   The codebase has 78+ direct fetch()/axios calls across 16 files.
//   Migrating each one to a wrapper is a lot of churn for the same
//   outcome. A patch lets Sprint 3.C ship in minutes, and any future
//   fetch() automatically benefits without the author thinking about it.
//
// Why limited to environment.apiURL:
//   We must NOT attach our JWT to requests aimed at Firebase Storage,
//   Vercel static assets, Google fonts, etc. Those are different trust
//   domains and would receive (and possibly log) our token.

import axios from 'axios';
import { environment } from '../environments';
import { getToken, clearAuth } from './auth';

let installed = false;

const isBackendUrl = (urlLike) => {
  if (!urlLike) return false;
  // Normalize: fetch accepts string | URL | Request.
  const href = typeof urlLike === 'string'
    ? urlLike
    : (urlLike.url || (urlLike.toString && urlLike.toString())) || '';

  // Same-origin relative paths starting with /api are ours.
  if (href.startsWith('/api')) return true;
  // Absolute paths to our backend host.
  try {
    const apiHost = new URL(environment.apiURL).host;
    const reqHost = new URL(href, window.location.origin).host;
    return reqHost === apiHost;
  } catch (_) {
    return false;
  }
};

const attachHeaderToInit = (init) => {
  const token = getToken();
  if (!token) return init;
  const next = { ...(init || {}) };
  // Headers can arrive as Headers, plain object, or array of pairs.
  const incoming = next.headers;
  if (incoming instanceof Headers) {
    if (!incoming.has('Authorization')) {
      incoming.set('Authorization', `Bearer ${token}`);
    }
    next.headers = incoming;
  } else if (Array.isArray(incoming)) {
    const hasAuth = incoming.some(([k]) => String(k).toLowerCase() === 'authorization');
    next.headers = hasAuth ? incoming : [...incoming, ['Authorization', `Bearer ${token}`]];
  } else {
    const obj = { ...(incoming || {}) };
    const hasAuth = Object.keys(obj).some((k) => k.toLowerCase() === 'authorization');
    if (!hasAuth) obj.Authorization = `Bearer ${token}`;
    next.headers = obj;
  }
  return next;
};

export const setupApiAuth = () => {
  if (installed) return;
  installed = true;

  // ---- fetch patch ---------------------------------------------------------
  if (typeof window !== 'undefined' && typeof window.fetch === 'function') {
    const originalFetch = window.fetch.bind(window);
    window.fetch = async (input, init) => {
      const targetIsBackend = isBackendUrl(input);
      const finalInit = targetIsBackend ? attachHeaderToInit(init) : init;
      const response = await originalFetch(input, finalInit);
      if (targetIsBackend && response && response.status === 401) {
        // Token rejected. Drop it so subsequent requests don't keep failing.
        clearAuth();
      }
      return response;
    };
  }

  // ---- axios interceptor ---------------------------------------------------
  axios.interceptors.request.use((config) => {
    if (isBackendUrl(config.url)) {
      const token = getToken();
      if (token) {
        config.headers = config.headers || {};
        if (!Object.keys(config.headers).some((k) => k.toLowerCase() === 'authorization')) {
          config.headers.Authorization = `Bearer ${token}`;
        }
      }
    }
    return config;
  });

  axios.interceptors.response.use(
    (response) => response,
    (error) => {
      if (error && error.response && error.response.status === 401) {
        const url = error.config && error.config.url;
        if (isBackendUrl(url)) clearAuth();
      }
      return Promise.reject(error);
    },
  );
};

export default setupApiAuth;
